import React, { useState } from 'react';
import theme from '../../theme/globalTheme';
import {
  Card,
  Button,
  Upload,
  message,
  Table,
  Divider,
  Alert,
  Space,
  Tag,
  Typography
} from 'antd';
import {
  UploadOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import { PDFDocument } from 'pdf-lib';

const { Title, Text } = Typography;

function PDFFieldValidator() {
  const [loading, setLoading] = useState(false);
  const [pdfInfo, setPdfInfo] = useState(null);
  const [fields, setFields] = useState([]);

  const handleFileUpload = async (file) => {
    setLoading(true);
    setPdfInfo(null);
    setFields([]);

    try {
      // Read file as ArrayBuffer
      const arrayBuffer = await file.arrayBuffer();

      // Load PDF
      const pdfDoc = await PDFDocument.load(arrayBuffer);

      console.log('=== PDF Validation Started ===');

      // Get basic PDF info
      const pageCount = pdfDoc.getPageCount();
      const title = pdfDoc.getTitle() || 'Sans titre';

      setPdfInfo({
        fileName: file.name,
        pageCount,
        title,
        size: (file.size / 1024).toFixed(2) + ' KB'
      });

      // Try to get form fields
      let formFields = [];
      let hasForm = false;

      try {
        const form = pdfDoc.getForm();
        const formFieldsList = form.getFields();
        hasForm = true;

        console.log(`Found ${formFieldsList.length} form fields`);

        formFields = formFieldsList.map((field, index) => {
          const fieldName = field.getName();
          const fieldType = field.constructor.name;

          let fieldValue = '';
          let isRequired = false;
          let isReadOnly = false;

          try {
            // Try to get field properties
            if (fieldType === 'PDFTextField') {
              fieldValue = field.getText() || '';
              isReadOnly = field.isReadOnly();
            } else if (fieldType === 'PDFCheckBox') {
              fieldValue = field.isChecked() ? 'Checked' : 'Unchecked';
            } else if (fieldType === 'PDFDropdown') {
              const options = field.getOptions();
              fieldValue = `Options: ${options.join(', ')}`;
            }
          } catch (e) {
            console.warn(`Could not get properties for field: ${fieldName}`);
          }

          return {
            key: index,
            name: fieldName,
            type: fieldType,
            value: fieldValue,
            isReadOnly,
            isRequired
          };
        });

        setFields(formFields);

        console.log('Field details:', formFields);

      } catch (e) {
        console.warn('No form fields found:', e.message);
        hasForm = false;
      }

      if (!hasForm) {
        message.warning('Ce PDF ne contient pas de champs de formulaire');
      } else {
        message.success(`${formFields.length} champ(s) trouvé(s) dans le PDF`);
      }

      console.log('=== PDF Validation Complete ===');

    } catch (error) {
      console.error('Error validating PDF:', error);
      message.error('Erreur lors de la validation du PDF: ' + error.message);
    } finally {
      setLoading(false);
    }

    return false; // Prevent auto upload
  };

  const columns = [
    {
      title: 'Nom du Champ',
      dataIndex: 'name',
      key: 'name',
      width: '30%',
      render: (name) => <Text strong copyable>{name}</Text>
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '20%',
      render: (type) => {
        let color = 'blue';
        if (type === 'PDFTextField') color = 'green';
        if (type === 'PDFCheckBox') color = 'orange';
        if (type === 'PDFDropdown') color = 'purple';
        return <Tag color={color}>{type.replace('PDF', '')}</Tag>;
      }
    },
    {
      title: 'Valeur Actuelle',
      dataIndex: 'value',
      key: 'value',
      width: '30%',
      render: (value) => <Text type="secondary">{value || '(vide)'}</Text>
    },
    {
      title: 'Propriétés',
      key: 'properties',
      width: '20%',
      render: (_, record) => (
        <Space>
          {record.isReadOnly && <Tag color="red">Lecture seule</Tag>}
          {record.isRequired && <Tag color="orange">Requis</Tag>}
        </Space>
      )
    }
  ];

  // Check if expected fields exist
  const checkExpectedFields = () => {
    const expectedFields = ['arrival_date', 'departure_date', 'arrival_hour', 'departure_hour'];

    return expectedFields.map(expected => {
      const found = fields.find(f => {
        const fieldNameLower = f.name.toLowerCase().replace(/[_\s-]/g, '');
        const expectedLower = expected.toLowerCase().replace(/[_\s-]/g, '');
        return fieldNameLower.includes(expectedLower) || expectedLower.includes(fieldNameLower);
      });

      return {
        name: expected,
        found: !!found,
        matchedField: found?.name
      };
    });
  };

  const expectedFieldsCheck = fields.length > 0 ? checkExpectedFields() : [];

  return (
    <div style={{ minHeight: '100vh', background: theme.colors.bgDark,  padding: '2rem' }}>
      <Card>
        <Title level={2}>Validateur de Champs PDF</Title>
        <Text type="secondary">
          Uploadez un fichier PDF pour voir tous les champs de formulaire disponibles
        </Text>

        <Divider />

        <Upload
          beforeUpload={handleFileUpload}
          accept=".pdf"
          showUploadList={false}
        >
          <Button icon={<UploadOutlined />} loading={loading} size="large" type="primary">
            Sélectionner un PDF
          </Button>
        </Upload>

        {pdfInfo && (
          <>
            <Divider>Informations du PDF</Divider>

            <Space direction="vertical" style={{ width: '100%' }} size="middle">
              <div>
                <Text strong>Nom du fichier: </Text>
                <Text>{pdfInfo.fileName}</Text>
              </div>
              <div>
                <Text strong>Nombre de pages: </Text>
                <Text>{pdfInfo.pageCount}</Text>
              </div>
              <div>
                <Text strong>Taille: </Text>
                <Text>{pdfInfo.size}</Text>
              </div>
            </Space>

            {fields.length > 0 && (
              <>
                <Divider>Vérification des Champs Requis</Divider>

                <Alert
                  message="Champs attendus pour Places à Quai"
                  description={
                    <Space direction="vertical" style={{ width: '100%', marginTop: '1rem' }}>
                      {expectedFieldsCheck.map((check) => (
                        <div key={check.name} style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          {check.found ? (
                            <CheckCircleOutlined style={{ color: theme.colors.white, fontSize: '18px' }} />
                          ) : (
                            <CloseCircleOutlined style={{ color: theme.colors.textTertiary, fontSize: '18px' }} />
                          )}
                          <Text strong>{check.name}</Text>
                          {check.found && (
                            <Text type="secondary">→ trouvé comme "{check.matchedField}"</Text>
                          )}
                          {!check.found && (
                            <Text type="danger">→ non trouvé</Text>
                          )}
                        </div>
                      ))}
                    </Space>
                  }
                  type={expectedFieldsCheck.every(c => c.found) ? 'success' : 'warning'}
                  showIcon
                  icon={expectedFieldsCheck.every(c => c.found) ? <CheckCircleOutlined /> : <InfoCircleOutlined />}
                />

                <Divider>Tous les Champs ({fields.length})</Divider>

                <Table
                  columns={columns}
                  dataSource={fields}
                  pagination={false}
                  bordered
                  size="small"
                />

                <Divider />

                <Alert
                  message="💡 Comment créer un PDF avec des champs de formulaire ?"
                  description={
                    <div style={{ marginTop: '0.5rem' }}>
                      <p>Pour que vos variables soient remplacées automatiquement, votre PDF doit contenir des champs de formulaire avec les noms suivants :</p>
                      <ul>
                        <li><code>arrival_date</code> ou <code>date_arrivee</code></li>
                        <li><code>departure_date</code> ou <code>date_depart</code></li>
                        <li><code>arrival_hour</code> ou <code>heure_arrivee</code></li>
                        <li><code>departure_hour</code> ou <code>heure_depart</code></li>
                      </ul>
                      <p><strong>Comment créer ces champs :</strong></p>
                      <ul>
                        <li>Utilisez Adobe Acrobat Pro : Outils → Préparer le formulaire → Ajouter un champ texte</li>
                        <li>Ou utilisez un service en ligne comme PDFescape ou Sejda</li>
                        <li>Nommez chaque champ exactement comme indiqué ci-dessus</li>
                      </ul>
                    </div>
                  }
                  type="info"
                  showIcon
                />
              </>
            )}

            {fields.length === 0 && (
              <Alert
                message="Aucun champ de formulaire trouvé"
                description="Ce PDF ne contient pas de champs de formulaire. Vous devez créer un PDF avec des champs de formulaire pour que les variables puissent être remplacées automatiquement."
                type="error"
                showIcon
                style={{ marginTop: '2rem' }}
              />
            )}
          </>
        )}
      </Card>
    </div>
  );
}

export default PDFFieldValidator;
